import React from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from "@sentry/react";
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { FronteggProvider } from '@frontegg/react';
import FronteggTokenSync from './components/FronteggTokenSync';

// Initialize Sentry for error tracking
// Disabled in staging and development; REACT_APP_SENTRY_ENABLED must be 'true' to send events
const sentryEnabled = process.env.REACT_APP_SENTRY_ENABLED === 'true';
if (!sentryEnabled) {
    console.log('[Sentry] Disabled in this environment (REACT_APP_SENTRY_ENABLED=%s)', process.env.REACT_APP_SENTRY_ENABLED || 'undefined');
}
Sentry.init({
    dsn: "https://f66be1866cd565bee441ba20ffff8298@o4510748564652032.ingest.us.sentry.io/4510748568846336",
    sendDefaultPii: true,
    environment: process.env.NODE_ENV,
    enabled: sentryEnabled,
    // Unactionable browser noise: connectivity blips and non-Error promise
    // rejections (Event objects from media/observer APIs)
    ignoreErrors: [
        'Network Error',
        'Non-Error promise rejection captured',
        /captured as promise rejection/,
        /ResizeObserver loop/,
    ],
});

if (sentryEnabled) {
    const CONSOLE_BRIDGE_DEDUP_WINDOW_MS = 60_000;
    const CONSOLE_BRIDGE_DEDUP_MAX_KEYS = 500;
    const seen = new Map();
    const originalError = console.error.bind(console);
    console.error = function patchedConsoleError(...args) {
        originalError(...args);
        try {
            // Caller-handled HTTP client errors (auth refresh/logout, RBAC, duplicate,
            // actionable validation) are expected control flow, not Sentry-worthy — the
            // API layer already excludes them from direct capture. Drop them here too so a
            // stray console.error on such a rejection (e.g. a 422 "not eligible for this
            // bucket") does not flood Sentry. Non-4xx errors still report normally.
            const EXPECTED_HTTP_STATUSES = new Set([401, 403, 409, 422]);
            const httpStatus = args
                .map(a => (a && (a.response?.status ?? a.status)))
                .find(s => Number.isInteger(s));
            if (EXPECTED_HTTP_STATUSES.has(httpStatus)) return;

            const errArg = args.find(a => a instanceof Error);
            const stringArgs = args.map(a => {
                if (a instanceof Error) return `${a.name}: ${a.message}`;
                if (typeof a === 'string') return a;
                if (a && typeof a === 'object') {
                    try { return JSON.stringify(a).slice(0, 500); } catch (_) { return '[object]'; }
                }
                return String(a);
            });
            const joined = stringArgs.join(' ').slice(0, 2000);
            const fingerprint = (errArg ? `${errArg.name}:${errArg.message}` : joined).slice(0, 300);
            if (!fingerprint || fingerprint.startsWith('[Sentry]')) return;
            const now = Date.now();
            const last = seen.get(fingerprint);
            if (last && now - last < CONSOLE_BRIDGE_DEDUP_WINDOW_MS) return;
            seen.set(fingerprint, now);
            if (seen.size > CONSOLE_BRIDGE_DEDUP_MAX_KEYS) {
                seen.delete(seen.keys().next().value);
            }
            Sentry.withScope((scope) => {
                scope.setTag('source', 'console.error');
                scope.setExtra('consoleArgs', stringArgs.map(s => s.slice(0, 500)));
                if (errArg) Sentry.captureException(errArg);
                else Sentry.captureMessage(joined || 'console.error', 'error');
            });
        } catch (_) { /* never let bridge crash */ }
    };
}

// Log build version for cache debugging
const BUILD_TIME = process.env.REACT_APP_BUILD_TIME || process.env.BUILD_TIME || 'dev';
const APP_VERSION = process.env.REACT_APP_VERSION || 'dev';
console.log(`%c🚀 Alchemiq v${APP_VERSION} - Build: ${BUILD_TIME}`,
    'color: #FF8C00; font-size: 14px; font-weight: bold;');

// Frontegg configuration (EU region, hosted login box)
const fronteggContextOptions = {
    baseUrl: process.env.REACT_APP_FRONTEGG_BASE_URL,
    clientId: process.env.REACT_APP_FRONTEGG_CLIENT_ID,
    appId: process.env.REACT_APP_FRONTEGG_APP_ID,
};

const fronteggAuthOptions = {
    keepSessionAlive: true, // Auto-refresh tokens
};

// Customize the Frontegg hosted login box signup form
// Signup must be enabled in Frontegg Dashboard -> Authentication -> Sign-ups
const fronteggThemeOptions = {
    loginBox: {
        signup: {
            hideSignUpFields: { hideCompanyName: true },
            splitFullName: false,
            passwordMeter: { enabled: true },
            requirePasswordConfirmation: true,
        },
        login: {
            docTitle: 'Alchemiq AI Login'
        },
    },
};

const fronteggEntitlementsOptions = {
    enabled: true, // Required for useFeatureEntitlements() to work
};

// Bridge component: provides react-router's navigate to Frontegg
function FronteggWithRouter({ children }) {
    const navigate = useNavigate();
    return (
        <FronteggProvider
            contextOptions={fronteggContextOptions}
            hostedLoginBox={true}
            authOptions={fronteggAuthOptions}
            themeOptions={fronteggThemeOptions}
            entitlementsOptions={fronteggEntitlementsOptions}
            navigate={navigate}
        >
            {children}
        </FronteggProvider>
    );
}

// Get the root element
const container = document.getElementById('root');

// Create a root
const root = createRoot(container);

// Initial render — BrowserRouter must be OUTSIDE FronteggProvider to avoid
// duplicate router conflict (history.ts:494 error)
root.render(
    <BrowserRouter>
        <FronteggWithRouter>
            <FronteggTokenSync />
            <DndProvider backend={HTML5Backend}>
                <App />
            </DndProvider>
        </FronteggWithRouter>
    </BrowserRouter>
);
